import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { type FC, useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants';
import { getUserSessionCountryCode } from 'dibs-regional-info/src/regionalInfoHelpers';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
// components
import { SellerReviewInformationLoading } from 'dibs-seller-reviews-modal';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel';

// lazy components
const PdpRespMarketplaceLayoutSecondaryLazy = dibsLazy(
    'PdpRespMarketplaceLayoutSecondary',
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceLayoutSecondary" */ './PdpRespMarketplaceLayoutSecondary'
        )
);

// types
import { type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables } from '../../../queries/pdpRespMarketplaceSecondaryRoot';

const LoadingCarousel: FC<{ isPdpSellerReviewsVariant: boolean | undefined }> = ({
    isPdpSellerReviewsVariant,
}) => {
    return (
        <>
            {isPdpSellerReviewsVariant && <SellerReviewInformationLoading />}
            <PdpRespMarketplaceLoadingCarousel />
            <PdpRespMarketplaceLoadingCarousel />
        </>
    );
};

export const PdpRespMarketplaceLayoutSecondaryRenderer: FC<{
    ssr: boolean;
    relayVariables: SecondaryRelayVariables;
}> = ({ ssr, relayVariables: serverRelayVariables }) => {
    const relayVariables = useRef(serverRelayVariables);
    const layoutVisibilityRef = useRef<HTMLDivElement>(null);
    const { isBot, isPdpSellerReviewsVariant } = useServerVarsContext();
    const [shouldRenderAfterDisplay, setShouldRenderAfterDisplay] = useState(false);

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const guestId = getGuestId(document.cookie) || '';
        const userCountryCode = getUserSessionCountryCode();

        const recentlyViewedItemIds = getLocalHistoryItemIds({
            isClient: true,
        }).slice(1, 3); // we need only 2 items but on pdp first item will be the current item

        relayVariables.current = {
            ...serverRelayVariables,
            userId,
            guestId,
            userCountryCode,
            afterDisplay: true,
            afterDisplayAndNotBot: !isBot,
            fetchUser: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            fetchSellerReviewsSection: !!isPdpSellerReviewsVariant,
            recentlyViewedItemIds:
                recentlyViewedItemIds.length >= 2
                    ? recentlyViewedItemIds
                    : [serverRelayVariables.itemId],
        };
    }, [isBot, serverRelayVariables, isPdpSellerReviewsVariant]);

    const scrollHandler = useCallback(() => setShouldRenderAfterDisplay(true), []);
    useEffect(() => {
        window.addEventListener('scroll', scrollHandler);
        return () => window.removeEventListener('scroll', scrollHandler);
    }, [scrollHandler]);

    useEffect(() => {
        if (shouldRenderAfterDisplay) {
            window.removeEventListener('scroll', scrollHandler);
        }
    }, [shouldRenderAfterDisplay, scrollHandler]);

    return (
        <>
            <VisibilityTracker
                elementRef={layoutVisibilityRef}
                onVisibilityChange={({ isVisible }) =>
                    isVisible && setShouldRenderAfterDisplay(true)
                }
            />
            <div ref={layoutVisibilityRef} />
            {ssr || shouldRenderAfterDisplay ? (
                <Suspense
                    fallback={
                        <LoadingCarousel isPdpSellerReviewsVariant={isPdpSellerReviewsVariant} />
                    }
                >
                    <PdpRespMarketplaceLayoutSecondaryLazy
                        relayVariables={relayVariables.current}
                        afterDisplay={shouldRenderAfterDisplay}
                    />
                </Suspense>
            ) : (
                <LoadingCarousel isPdpSellerReviewsVariant={isPdpSellerReviewsVariant} />
            )}
        </>
    );
};
